import { useState, useEffect, useCallback, useRef } from 'react';
import { logger } from '../utils/logger';

function useLocalStorage<T>(key: string, initialValue: T) {
  // Use a ref to store the initial value to avoid dependency changes on every render
  const initialValueRef = useRef(initialValue);

  // Get from local storage then parse stored json or return initialValue
  const readValue = useCallback((): T => {
    if (typeof window === 'undefined') {
      return initialValueRef.current;
    }
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValueRef.current;
    } catch (error) {
      logger.warn(`Error reading localStorage key "${key}":`, error);
      return initialValueRef.current;
    }
  }, [key]);

  const [storedValue, setStoredValue] = useState<T>(readValue);

  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to localStorage.
  const setValue = (value: T | ((val: T) => T)) => {
    try {
      // Allow value to be a function so we have same API as useState
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      
      // Save state
      setStoredValue(valueToStore);
      
      // Save to local storage
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(key, JSON.stringify(valueToStore));
      }
    } catch (error) {
      logger.warn(`Error setting localStorage key "${key}":`, error);
    }
  };

  // Listen for changes elsewhere (optional, but good for sync)
  useEffect(() => {
    const handleStorageChange = (event: globalThis.StorageEvent) => {
      // Only update if the change is for our specific key
      if (event.key === key && event.newValue !== null) {
        try {
          const newValue = JSON.parse(event.newValue) as T;
          setStoredValue(newValue);
        } catch (error) {
          logger.warn(`Error parsing localStorage change for key "${key}":`, error);
        }
      }
    };

    // Note: 'storage' event only triggers for other tabs/windows, not the current one.
    // For single-page app state sync, the internal state mainly handles it.
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [key]);

  return [storedValue, setValue] as const;
}

export default useLocalStorage;